import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AppService } from '../../../app.service';
import {
  AdminGridDirective,
  AdminGridConfig,
  AdminGridCallbackEvent,
} from '../../shared/admin-grid.directive';
import { AdminJobConsoleEditComponent } from './admin-job-console-edit/admin-job-console-edit.component';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';

@Component({
    selector: 'gtw-admin-job-console-list',
    templateUrl: './admin-job-console-list.component.html',
    styleUrls: ['./admin-job-console-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminJobConsoleListComponent implements OnInit, OnDestroy {
  @ViewChildren(AdminGridDirective) grids!: QueryList<AdminGridDirective>;
  @ViewChild('jobsGrid') jobsGrid?: AdminGridDirective;
  @ViewChild('logHistoryGrid') logHistoryGrid?: AdminGridDirective;

  jobsGridConfig!: AdminGridConfig;
  logHistoryGridConfig!: AdminGridConfig;

  /** Tab indices: 0 = Job Grids, 1 = Job Log History. */
  activeTab = 0;
  jobsTabHover = false;
  logsTabHover = false;

  /** Per-grid loading flags (shown as overlay spinners). */
  jobsLoading = false;
  logsLoading = false;

  private readonly destroy$ = new Subject<void>();

  // --- Original AngularJS routing endpoints ---
  // Tab 1 (Job Grids):       loadGridJson?action_id=32584&grid_id=5898
  // Tab 2 (Job Log History): loadGridJson?action_id=32592&grid_id=5902
  private readonly JOB_ACTION_ID = 32584;
  private readonly JOB_GRID_ID = '5898';
  private readonly LOG_ACTION_ID = 32592;
  private readonly LOG_GRID_ID = '5902';

  constructor(
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private dialog: MatDialog,
    private adminScreenContext: AdminScreenContextService,
  ) {}

  ngOnInit(): void {
    const clientKey =
      this.appService.USER_SETTINGS?.user?.client?.client_key ??
      this.appService.USER_SETTINGS?.user?.client_key ??
      '';

    this.jobsGridConfig = {
      actionId: this.JOB_ACTION_ID,
      gridId: this.JOB_GRID_ID,
      extraParams: { client_key: clientKey },
      autoLoad: true,
      cacheKey: 'admin/job-console-list/jobs',
    };

    this.logHistoryGridConfig = {
      actionId: this.LOG_ACTION_ID,
      gridId: this.LOG_GRID_ID,
      extraParams: { client_key: clientKey },
      autoLoad: true,
      cacheKey: 'admin/job-console-list/log-history',
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTabChange(index: number): void {
    if (this.activeTab === index) return;
    this.activeTab = index;
    this.cdr.detectChanges();
  }

  onGridCallback(event: AdminGridCallbackEvent): void {
    const rowData = event?.data?.rowData ?? event?.data;
    if (!rowData) return;
    this.openEditModal(rowData);
  }

  onGridDataLoaded(_data: any): void {
    this.cdr.detectChanges();
  }

  onJobsLoadingChange(isLoading: boolean): void {
    this.jobsLoading = isLoading;
    this.cdr.detectChanges();
  }

  onLogsLoadingChange(isLoading: boolean): void {
    this.logsLoading = isLoading;
    this.cdr.detectChanges();
  }

  /** Combined flag used to spin the toolbar refresh icon. */
  get loading(): boolean {
    return this.activeTab === 0 ? this.jobsLoading : this.logsLoading;
  }

  refresh(): void {
    const grid = this.activeTab === 0 ? this.jobsGrid : this.logHistoryGrid;
    grid?.clearCache();
    grid?.refresh();
  }

  private openEditModal(rowData: any): void {
    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '90vw';
    config.maxWidth = '1000px';
    config.disableClose = true;
    config.data = { rowData: rowData || {} };

    this.adminScreenContext.setRowData(rowData);
    const ref = this.dialog.open(AdminJobConsoleEditComponent, config);
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.adminScreenContext.setRowData({});
        if (result === 'saved') {
          this.jobsGrid?.clearCache();
          this.jobsGrid?.refresh();
          this.logHistoryGrid?.clearCache();
          this.logHistoryGrid?.refresh();
        }
      });
  }
}
